import React, { useCallback, useEffect, useRef, useState } from 'react'
import { PublicationCardItemWithVideo } from '@news-mono/web-common'
import { VideoMetaDTO } from '@west-australian-newspapers/publication-types'

import { Skeleton } from '../content'
import {
    StyledWrapper,
    StyledScroll,
    StyledButtonLeft,
    StyledButtonRight,
} from './VerticalVideoCarousel.styled'
import { VerticalVideoCard } from '../SevenNewsV2/cards/VerticalVideoCard/VerticalVideoCard'
import {
    StyledSkeletonItem,
    StyledSkeletonMedia,
} from '../SevenNewsV2/cards/VerticalVideoCard/VerticalVideoCard.styled'

interface VerticalVideoCarouselProps {
    videos: VideoMetaDTO[]
    cardItems: PublicationCardItemWithVideo[] // ← DODANE
    onSelect: (video: VideoMetaDTO) => void
    isLoading?: boolean
    skeletonCount?: number
}

const arrowIcon = require('../SevenNewsV2/assets/arrowIcon.svg')

export const VerticalVideoCarousel: React.FC<VerticalVideoCarouselProps> = ({
    videos,
    cardItems,
    onSelect,
    isLoading = false,
    skeletonCount = 5,
}) => {
    const scrollRef = useRef<HTMLDivElement | null>(null)
    const itemRef = useRef<HTMLDivElement | null>(null)

    const [showButtons, setShowButtons] = useState(videos.length > 5)
    const [canScrollPrev, setCanScrollPrev] = useState(false)
    const [canScrollNext, setCanScrollNext] = useState(videos.length > 5)

    const [itemWidth, setItemWidth] = useState(0)
    const [gap, setGap] = useState(0)
    const [currentIndex, setCurrentIndex] = useState(0)

    const [isTouchDevice, setIsTouchDevice] = useState(false)

    useEffect(() => {
        setIsTouchDevice(
            'ontouchstart' in window || navigator.maxTouchPoints > 0,
        )
    }, [])

    useEffect(() => {
        if (itemRef.current) {
            setItemWidth(itemRef.current.clientWidth)
        }

        if (scrollRef.current) {
            const styles = window.getComputedStyle(scrollRef.current)
            const parsed = parseFloat(styles.columnGap || styles.gap || '0')
            setGap(isNaN(parsed) ? 0 : parsed)
        }
    }, [videos])

    const updateButtons = useCallback(() => {
        const el = scrollRef.current
        if (!el || itemWidth === 0) return

        const maxIndex = videos.length - 1
        const totalCardsWidth =
            videos.length * itemWidth + Math.max(videos.length - 1, 0) * gap
        const isOverflowing = totalCardsWidth > el.clientWidth
        setShowButtons(isOverflowing)

        if (!isOverflowing) {
            setCanScrollPrev(false)
            setCanScrollNext(false)
            return
        }

        setCanScrollPrev(currentIndex > 0)
        setCanScrollNext(currentIndex < maxIndex)
    }, [currentIndex, gap, itemWidth, videos.length])

    useEffect(() => {
        updateButtons()
    }, [updateButtons])

    const scrollToIndex = (index: number) => {
        const el = scrollRef.current
        if (!el || itemWidth === 0) return

        const step = itemWidth + gap

        el.scrollTo({
            left: index * step,
            behavior: 'smooth',
        })

        setCurrentIndex(index)
    }

    const scrollByCard = (dir: 'prev' | 'next') => {
        const maxIndex = videos.length - 1

        if (dir === 'next' && currentIndex < maxIndex)
            scrollToIndex(currentIndex + 1)
        else if (dir === 'prev' && currentIndex > 0)
            scrollToIndex(currentIndex - 1)
    }

    if (isLoading) {
        const placeholders = Array.from({ length: skeletonCount })

        return (
            <StyledWrapper>
                <StyledScroll
                    ref={scrollRef}
                    data-disable-drag={!isTouchDevice}
                >
                    {placeholders.map((_, idx) => (
                        <StyledSkeletonItem
                            key={`vertical-skeleton-${idx}`}
                            ref={idx === 0 ? itemRef : undefined}
                        >
                            <StyledSkeletonMedia>
                                <Skeleton count={1} />
                            </StyledSkeletonMedia>
                        </StyledSkeletonItem>
                    ))}
                </StyledScroll>
            </StyledWrapper>
        )
    }

    return (
        <StyledWrapper>
            {showButtons && (
                <StyledButtonLeft
                    type="button"
                    onClick={() => scrollByCard('prev')}
                    disabled={!canScrollPrev}
                >
                    <img src={arrowIcon} alt="Scroll" />
                </StyledButtonLeft>
            )}

            <StyledScroll ref={scrollRef} data-disable-drag={!isTouchDevice}>
                {videos.map((video, idx) => {
                    const cardItem = cardItems[idx]

                    return (
                        <VerticalVideoCard
                            key={video.id ?? idx}
                            cardItem={cardItem}
                            videoMeta={video}
                            isLoading={isLoading}
                            onSelect={() => onSelect(video)}
                            innerRef={idx === 0 ? itemRef : undefined}
                        />
                    )
                })}
            </StyledScroll>

            {showButtons && (
                <StyledButtonRight
                    type="button"
                    onClick={() => scrollByCard('next')}
                    disabled={!canScrollNext}
                >
                    <img src={arrowIcon} alt="Scroll" />
                </StyledButtonRight>
            )}
        </StyledWrapper>
    )
}
